import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CreateFinancingBody, FinancingStageBody } from '@brimo/shared';
import { ApiError, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type {
  BoardResponse,
  CommissionPreview,
  CreateOptions,
  CreateResponse,
  DecideResponse,
  FinancierChangeResponse,
  FinancingRecord,
  HistoryResponse,
  LinkOption,
  ListFilters,
  ListResponse,
  LostReason,
  StageOptionsResponse,
} from './types';

export type { FinancingRecord } from './types';

/**
 * Queries and mutations of the financings module (docs/dev/api-financings.md). Keys live under the roots
 * `['financings']` (list, board, options) and `['financing', id]` (record and its tabs) so that the realtime
 * event `financing.updated` refreshes them.
 */
export const financingKeys = {
  list: (filters: ListFilters) => [...queryRoots.financings, 'list', filters] as const,
  board: (q: string) => [...queryRoots.financings, 'board', q] as const,
  createOptions: (params: { dealId?: string | undefined; contactId?: string | undefined }) =>
    [...queryRoots.financings, 'create-options', params.dealId ?? '', params.contactId ?? ''] as const,
  commissionPreview: (dealId: string, ratePct: number, amountCents: number) =>
    [...queryRoots.financings, 'commission-preview', dealId, ratePct, amountCents] as const,
  lostReasons: () => [...queryRoots.financings, 'lost-reasons'] as const,
  record: (id: string) => [...queryRoots.financing, id] as const,
  history: (id: string) => [...queryRoots.financing, id, 'history'] as const,
  stageOptions: (id: string) => [...queryRoots.financing, id, 'stage-options'] as const,
  linkOptions: (id: string) => [...queryRoots.financing, id, 'link-options'] as const,
};

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
}

function notFoundOrForbidden(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

// --- list and board ------------------------------------------------------------------------------------

export function useFinancingList(filters: ListFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: financingKeys.list(filters),
    queryFn: ({ pageParam, signal }) =>
      api<ListResponse>(
        `/financings${query({
          stage: filters.stage ?? undefined,
          withoutDeal: filters.withoutDeal ? '1' : undefined,
          q: filters.q || undefined,
          cursor: pageParam ?? undefined,
        })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    enabled,
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 15_000,
  });
}

export function useFinancingBoard(q: string, enabled = true) {
  return useQuery({
    queryKey: financingKeys.board(q),
    queryFn: ({ signal }) =>
      api<BoardResponse>(`/financings/board${query({ q: q || undefined })}`, { signal }),
    placeholderData: keepPreviousData,
    enabled,
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 15_000,
  });
}

// --- create --------------------------------------------------------------------------------------------

export function useCreateOptions(params: { dealId?: string | undefined; contactId?: string | undefined }) {
  const enabled = Boolean(params.dealId || params.contactId);
  return useQuery({
    queryKey: financingKeys.createOptions(params),
    queryFn: ({ signal }) =>
      api<CreateOptions>(
        `/financings/create-options${query({ dealId: params.dealId, contactId: params.dealId ? undefined : params.contactId })}`,
        { signal },
      ),
    enabled,
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 30_000,
  });
}

export function useCommissionPreview(
  dealId: string | null,
  ratePct: number | null,
  amountCents: number | null,
) {
  const enabled = dealId !== null && ratePct !== null && amountCents !== null;
  return useQuery({
    queryKey: financingKeys.commissionPreview(dealId ?? '', ratePct ?? 0, amountCents ?? 0),
    queryFn: ({ signal }) =>
      api<CommissionPreview>(
        `/financings/commission-preview${query({
          dealId: dealId ?? undefined,
          ratePct: String(ratePct ?? 0),
          amountCents: String(amountCents ?? 0),
        })}`,
        { signal },
      ),
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}

export function createFinancing(body: CreateFinancingBody): Promise<CreateResponse> {
  return api<CreateResponse>('/financings', { method: 'POST', body });
}

// --- record --------------------------------------------------------------------------------------------

export function useFinancing(id: string): UseQueryResult<FinancingRecord, Error> {
  return useQuery({
    queryKey: financingKeys.record(id),
    queryFn: ({ signal }) => api<FinancingRecord>(`/financings/${id}`, { signal }),
    retry: (count, error) => !notFoundOrForbidden(error) && count < 1,
    staleTime: 15_000,
  });
}

export function useFinancingHistory(id: string, enabled: boolean) {
  return useQuery({
    queryKey: financingKeys.history(id),
    queryFn: ({ signal }) => api<HistoryResponse>(`/financings/${id}/history`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useStageOptions(id: string, enabled: boolean) {
  return useQuery({
    queryKey: financingKeys.stageOptions(id),
    queryFn: ({ signal }) => api<StageOptionsResponse>(`/financings/${id}/stage-options`, { signal }),
    enabled,
    staleTime: 0,
  });
}

export function useLostReasons(enabled: boolean) {
  return useQuery({
    queryKey: financingKeys.lostReasons(),
    queryFn: ({ signal }) => api<{ items: LostReason[] }>('/financings/lost-reasons', { signal }),
    enabled,
    staleTime: 300_000,
  });
}

export function useLinkOptions(id: string, enabled: boolean) {
  return useQuery({
    queryKey: financingKeys.linkOptions(id),
    queryFn: ({ signal }) => api<{ items: LinkOption[] }>(`/financings/${id}/link-options`, { signal }),
    enabled,
    staleTime: 0,
  });
}

// --- transitions ---------------------------------------------------------------------------------------

export function changeFinancingStage(id: string, body: FinancingStageBody): Promise<FinancingRecord> {
  return api<FinancingRecord>(`/financings/${id}/stage`, { method: 'POST', body });
}

export function loseFinancing(
  id: string,
  body: { lostReasonId: string; note?: string },
): Promise<FinancingRecord> {
  return api<FinancingRecord>(`/financings/${id}/lose`, { method: 'POST', body });
}

export function reopenFinancing(id: string, reason: string): Promise<FinancingRecord> {
  return api<FinancingRecord>(`/financings/${id}/reopen`, { method: 'POST', body: { reason } });
}

export function linkFinancingDeal(id: string, dealId: string): Promise<FinancingRecord> {
  return api<FinancingRecord>(`/financings/${id}/link-deal`, { method: 'POST', body: { dealId } });
}

export function changeFinancier(id: string, financierId: string): Promise<FinancierChangeResponse> {
  return api<FinancierChangeResponse>(`/financings/${id}/financier`, {
    method: 'POST',
    body: { financierId },
  });
}

export function decideAssignment(
  id: string,
  body: { approvalId: string; decision: 'approved' | 'rejected'; reason?: string },
): Promise<DecideResponse> {
  return api<DecideResponse>(`/financings/${id}/assignment/decide`, { method: 'POST', body });
}

export function patchFinancingRate(id: string, commissionRatePct: number): Promise<FinancingRecord> {
  return api<FinancingRecord>(`/financings/${id}`, { method: 'PATCH', body: { commissionRatePct } });
}

/** Refreshes the module after a mutation (the realtime event does the same for the other tabs). */
export function useInvalidateFinancings() {
  const queryClient = useQueryClient();
  return async (financingId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...queryRoots.financings] }),
      financingId
        ? queryClient.invalidateQueries({ queryKey: financingKeys.record(financingId) })
        : queryClient.invalidateQueries({ queryKey: [...queryRoots.financing] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.deal] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.deals] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.tasks] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.approvals] }),
    ]);
  };
}
